import {
    computed,
    shallowRef,
    type ComputedRef,
    onBeforeUnmount,
    type ShallowRef,
} from 'vue'
import { useEventListener } from '@vueuse/core'

import { getDocument, getTimeout, getWindow } from '@/helpers/globals'
import {
    clone,
    create,
    identity,
    scale as scaleM,
    toCSSString,
    translate,
} from '@/utils/mat2d'
import { useIsMobileAgent } from '@/plugins/is-mobile-agent'

export const usePinchToZoom = (
    elementRef: Readonly<ShallowRef<HTMLDivElement[] | null>>,
    zoomOutOnRelease: boolean
): {
    isZoomed: ComputedRef<boolean>
} => {
    const document = getDocument()
    const window = getWindow()
    const setTimeout = getTimeout()?.setTimeout
    const isMobile = useIsMobileAgent()

    if (!document || !window || !setTimeout || !isMobile) {
        return {
            isZoomed: computed(() => false),
        }
    }

    const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
    }

    const start = {
        x: 0,
        y: 0,
        clientX: 0,
        clientY: 0,
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        distance: 0,
        width: 0,
        height: 0,
        originX: 0,
        originY: 0,
        originToCenterX: 0,
        originToCenterY: 0,
    }

    let scale = 1

    // Prevent single-touch events from interrupting pinch-to-zoom
    let currentTouchCount = 0

    const zoomedElement = shallowRef<HTMLElement | null>(null)
    let originalElement: HTMLElement | null = null

    const mInitial = create()
    const mOriginPre = create()
    const mOriginPost = create()
    const mScaled = create()
    const mResult = create()
    const mPrevious = create()

    const assignScale = (s: number) => {
        if (s > scale && mResult[0] >= 4) {
            return
        }

        scale = s
    }

    if (document) {
        const handleTouchStart = (event: TouchEvent, isDoubleTap = false) => {
            if (currentTouchCount > 0 && !isDoubleTap) {
                return
            }

            if (event.touches.length !== 2 && event.touches.length !== 1) {
                return
            }

            const previousScale = mPrevious[0]
            const fingerOne = event.touches[0]
            let fingerTwo = event.touches[1]

            if (event.touches.length === 1) {
                if (previousScale <= 1 && !isDoubleTap) {
                    return
                }

                fingerTwo = fingerOne
            }

            if (!fingerOne || !fingerTwo) {
                return
            }

            if (event.touches.length === 2) {
                event.preventDefault?.() // Prevent page scroll
            }

            // Calculate where the fingers have started on the X and Y axis
            start.x = (fingerOne.clientX + fingerTwo.clientX) / 2
            start.y = (fingerOne.clientY + fingerTwo.clientY) / 2
            // Transform origin
            start.clientX = (fingerOne.clientX + fingerTwo.clientX) / 2
            start.clientY = (fingerOne.clientY + fingerTwo.clientY) / 2

            start.distance = getDistance(fingerOne, fingerTwo)

            const element = event.currentTarget as HTMLElement
            const rectangle = element.getBoundingClientRect()

            start.width = rectangle.width
            start.height = rectangle.height

            start.left = rectangle.left
            start.right = rectangle.right

            start.top = rectangle.top
            start.bottom = rectangle.bottom

            start.originX = start.clientX - rectangle.left
            start.originY = start.clientY - rectangle.top

            start.originToCenterX =
                start.clientX - rectangle.left - start.width / 2
            start.originToCenterY =
                start.clientY - rectangle.top - start.height / 2

            if (!zoomedElement.value) {
                zoomedElement.value = element.cloneNode(true) as HTMLElement
                zoomedElement.value.style.position = 'fixed'
                zoomedElement.value.style.zIndex = '9999'
                zoomedElement.value.style.width = `${rectangle.width}px`
                zoomedElement.value.style.height = `${rectangle.height}px`
                zoomedElement.value.style.top = `${rectangle.top}px`
                zoomedElement.value.style.left = `${rectangle.left}px`
                zoomedElement.value.style.transform = 'matrix(1, 0, 0, 1, 0, 0)'

                zoomedElement.value.addEventListener('dblclick', (event) => {
                    event.preventDefault()
                    event.stopPropagation()

                    const touchmove = mouseToTouch(event)

                    identity(mResult)
                    onTouchEnd(touchmove, true)
                })
                zoomedElement.value.addEventListener(
                    'touchstart',
                    handleTouchStart
                )
                zoomedElement.value.addEventListener(
                    'touchmove',
                    handleTouchMove
                )
                zoomedElement.value.addEventListener('touchcancel', onTouchEnd)
                zoomedElement.value.addEventListener('touchend', onTouchEnd)
                originalElement = element

                element.style.pointerEvents = 'none'
                element.style.opacity = '0'

                document.body.appendChild(zoomedElement.value)
            }
        }

        const handleTouchMove = (event: TouchEvent, doubleTapScale = 0) => {
            if (currentTouchCount > 0 && !doubleTapScale) {
                return
            }

            if (!zoomedElement.value) {
                return
            }

            if (event.touches.length !== 2 && event.touches.length !== 1) {
                return
            }

            const fingerOne = event.touches[0]
            let fingerTwo = event.touches[1]
            const previousScale = mPrevious[0]

            if (event.touches.length === 1) {
                if (previousScale <= 1 && !doubleTapScale) {
                    return
                }

                fingerTwo = fingerOne
            }

            if (!fingerOne || !fingerTwo) {
                return
            }

            if (event.touches.length === 2) {
                event.preventDefault?.() // Prevent page scroll
            }

            if (mResult[0] < 1) {
                onTouchEnd(event)

                return
            }

            if (doubleTapScale) {
                assignScale(doubleTapScale / previousScale)
                zoomedElement.value.style.transition = 'transform 0.3s ease'
                setTimeout(() => {
                    if (!zoomedElement.value) {
                        return
                    }

                    zoomedElement.value.style.transition = ''
                }, 300)

                // Safari provides event.scale as two fingers move on the screen
                // For other browsers just calculate the scale manually
            } else if ('scale' in event && typeof event.scale === 'number') {
                const s = Math.min(event.scale, 4)

                assignScale(s)
            } else {
                const deltaDistance = getDistance(fingerOne, fingerTwo)

                const s =
                    Math.min(
                        Math.max(deltaDistance / start.distance, -100),
                        4
                    ) || 1

                assignScale(s)
            }

            const scaleInverted = scale - 1

            const leftMax = start.originX * scaleInverted - start.left

            const leftMin =
                viewport.width -
                (start.right + (start.width - start.originX) * scaleInverted)

            const topMax = Math.max(
                0,
                start.originY * scaleInverted - start.top
            )

            const topMin = Math.min(
                0,
                viewport.height -
                    start.bottom +
                    (start.height - start.originY) * scaleInverted
            )

            const deltaX = (fingerOne.clientX + fingerTwo.clientX) / 2 - start.x
            const deltaXClamped = clamp(deltaX, leftMin, leftMax)
            const deltaY = (fingerOne.clientY + fingerTwo.clientY) / 2 - start.y
            const deltaYClamped = clamp(deltaY, topMin, topMax)

            translate(
                mResult,
                translate(
                    mOriginPost,
                    scaleM(
                        mScaled,
                        translate(mOriginPre, mPrevious, [
                            start.originToCenterX / previousScale,
                            start.originToCenterY / previousScale,
                        ]),
                        [scale, scale]
                    ),
                    [
                        -start.originToCenterX / previousScale,
                        -start.originToCenterY / previousScale,
                    ]
                ),
                [
                    deltaXClamped / (scale * previousScale),
                    deltaYClamped / (scale * previousScale),
                ]
            )

            const transform = toCSSString(mResult)

            const t = () => {
                if (!zoomedElement.value) {
                    return
                }

                zoomedElement.value.style.transform = transform
            }

            if (doubleTapScale) {
                setTimeout(t)
            } else {
                t()
            }
        }

        useEventListener(elementRef, 'touchstart', handleTouchStart)
        useEventListener(elementRef, 'touchmove', handleTouchMove)
        useEventListener(elementRef, 'dblclick', (event) => {
            const touchstart = mouseToTouch(event)
            const touchmove = mouseToTouch(event)

            handleTouchStart(touchstart, true)
            handleTouchMove(touchmove, 4)
            onTouchEnd(touchmove, true)
        })

        const removeZoomedElement = () => {
            if (!zoomedElement.value || !originalElement) {
                return
            }

            zoomedElement.value.style.transform = ''
            document.body.removeChild(zoomedElement.value)
            zoomedElement.value = null
            originalElement.style.opacity = '1'
            originalElement.style.pointerEvents = 'auto'
        }

        onBeforeUnmount(removeZoomedElement)

        const onTouchEnd = (event: TouchEvent, isDoubleTap = false) => {
            currentTouchCount = isDoubleTap ? 0 : event.touches.length

            if (zoomedElement.value && (mResult[0] <= 1 || zoomOutOnRelease)) {
                identity(mInitial)
                identity(mOriginPre)
                identity(mOriginPost)
                identity(mScaled)
                identity(mResult)
                identity(mPrevious)
                scale = 1

                if (isDoubleTap || zoomOutOnRelease) {
                    zoomedElement.value.style.transition = 'transform 0.3s ease'
                    zoomedElement.value.style.transform =
                        'matrix(1, 0, 0, 1, 0, 0)'
                    setTimeout(removeZoomedElement, 300)
                } else {
                    removeZoomedElement()
                }
            } else {
                clone(mPrevious, mResult)
            }
        }

        useEventListener(elementRef, 'touchend', onTouchEnd)
        useEventListener(elementRef, 'touchcancel', onTouchEnd)
    }

    return {
        isZoomed: computed(() => Boolean(zoomedElement.value)),
    }
}

const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max)

// Calculate distance between two fingers
const getDistance = (fingerOne: Touch, fingerTwo: Touch) =>
    Math.hypot(
        fingerOne.clientX - fingerTwo.clientX,
        fingerOne.clientY - fingerTwo.clientY
    )

const mouseToTouch = (e: MouseEvent): TouchEvent => {
    const touch = {
        clientX: e.clientX,
        clientY: e.clientY,
        currentTarget: e.target || e.currentTarget,
    }

    const te = {
        touches: [touch],
        currentTarget: e.target || e.currentTarget,
    }

    return te as unknown as TouchEvent
}
